import config from '@config';
import state from '@state';
import { getPauseIcon, getPlayIcon } from '@utils/icons';
import { formatPhoneNumbersForTTS } from '@utils/digitsToWords';
import i18n from '@i18n';
import { smartFetch } from '@utils/smartFetch';
import { wait } from '@query/processor';
import { TAudioResponse } from 'types';
/**
 * Initializes the "Read Aloud" functionality for a response.
 * Adds a click event listener to the "Read Aloud" button to fetch and play audio for the provided text content.
 *
 * @param textContent - The text content to convert to speech and play.
 */
export function initReadAloud(textContent: string): void {
	const button = document.querySelector('.aex-response__read-aloud-button') as HTMLButtonElement | null;

	if (!button) return;

	let isPlaying = false;

	/**
	 * Updates the button's icon and ARIA attributes based on the playback state.
	 *
	 * @param playing - Whether the audio is currently playing.
	 */
	const updateIcon = (playing: boolean): void => {
		button.setAttribute('aria-pressed', playing ? 'true' : 'false');
		button.innerHTML = playing
			? getPauseIcon()
			: getPlayIcon();
	};

	button.addEventListener('click', async () => {
		if (isPlaying && state.currentAudio) {
			state.currentAudio.pause();
			isPlaying = false;
			updateIcon(false);
			return;
		} else {
			updateIcon(true);

			try {
				// Cancel any existing query
				state.abortManager.cancelCurrentRequest();
				document.querySelector(`.aex-response-${state.currentInstance?.index} .aex-response__error`)?.remove();

				// Create a new abort signal for this query
				const signal = state.abortManager.createAbortSignal();

				button.classList.add('aex-response__loading-spinner');

				const response = await smartFetch(config.readAloudUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'agent_id': config.agentId,
						Accept: 'audio/*',
					},
					body: JSON.stringify({
						text: formatPhoneNumbersForTTS(textContent, { zeroAsOh: true }),
					}),
					signal: signal,
				});

				if (!response.ok) {
					throw new Error(i18n.t('error.audioFetchError'));
				}

				const result = await response.json();
				const taskId = result.id;

				if (!taskId) {
					throw new Error(i18n.t('error.invalidAudioResponse'));
				}

				// Let's poll for the status.
				const pResults = await pollForAudio(taskId, signal);
				if (!pResults) {
					throw new Error(i18n.t('error.audioFetchError'));
				}

				// Create the file data URL for playback.
				const playbackUrl = config.readAloudFileUrl.replace('{taskId}', taskId);
				const mResponse = await fetch(playbackUrl, {
					headers: {
						'agent_id': config.agentId,
						Accept: 'audio/*',
					},
				});

				// If the audio isn't ready or there's an error, handle it gracefully.
				if (!mResponse.ok) {
					throw new Error(i18n.t('error.audioFetchError'));
				}

				const blob = await mResponse.blob();
				const url = URL.createObjectURL(blob);
				state.currentAudio = new Audio(url);
				button.classList.remove('aex-response__loading-spinner');
			} catch (err) {
				console.error(i18n.t('error.ttsFetchError', { prefix: i18n.t('log.prefix') }), err);
				button.classList.remove('aex-response__loading-spinner');

				const audioError = document.createElement('div');
				audioError.className = 'aex-response__error';
				audioError.innerHTML = `<div role="alert">${i18n.t('error.textToSpeechUnavailable')}</div>`;
				button.parentElement?.insertAdjacentElement('afterend', audioError);
				updateIcon(false);
				return;
			} finally {
				button.disabled = false;
			}
		}

		state.currentAudio!.play();
		isPlaying = true;
		updateIcon(true);

		state.currentAudio!.onended = () => {
			isPlaying = false;
			updateIcon(false);
		};

		state.currentAudio!.onerror = () => {
			console.error(i18n.t('error.ttsPlaybackError', { prefix: i18n.t('log.prefix') }));
			isPlaying = false;
			updateIcon(false);
		};
	});

	updateIcon(false);
}

export async function pollForAudio(taskId: string, signal: AbortSignal): Promise<boolean | undefined> {

	// Define the count and has result status.
	const pollingUrl = config.pollingUrl.replace('{taskId}', taskId);
	let count = 0;

	// Max wait: 2 minutes.
	while (count <= 24) {
		const pResponse = await smartFetch(pollingUrl, {
			method: 'GET',
			signal: signal,
		});

		if (!pResponse.ok) throw new Error(`${i18n.t('error.HTTPError')}: ${pResponse.status}`);

		const pResults = await pResponse.json();
		if (pResults.status === 'pending') {
			count++;
			await wait(5_000);
			continue;
		}

		return pResults.status;
	}

	return false;
}

export function audioResponseToBlob(response: TAudioResponse): Blob {
	const mimeType = `audio/${response.audio_format}`;
	const binary = atob(response.audio_base64);

	const bytes = new Uint8Array(binary.length);

	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}

	return new Blob([bytes], {
		type: mimeType,
	});
};
